import { Clock, GraduationCap, MapPin, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface BasicInformationCardProps {
  newCourse: any;
  setNewCourse: (course: any) => void;
  eventTypeOptions: { value: string; label: string }[];
  targetAudienceOptions: { value: string; label: string }[];
}

export const BasicInformationCard = ({
  newCourse,
  setNewCourse,
  eventTypeOptions,
  targetAudienceOptions
}: BasicInformationCardProps) => {
  // Memoize the handlers to prevent recreating on each render
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) => {
    setNewCourse((prev: any) => ({ ...prev, [field]: e.target.value }));
  }, [setNewCourse]);

  const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    // Don't allow editing the hours field as it's now calculated from requirements
    if (field === 'hours') return;
    
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === '' || !isNaN(Number(value))) {
      // Check for negative numbers
      if (value !== '' && Number(value) < 0) {
        return;
      }
      // Check for valid hours (between 0 and 100)
      if (field === 'hours' && value !== '' && Number(value) > 100) {
        return;
      }
      // Check for reasonable max participants (between 0 and 1000)
      if (field === 'max_participants' && value !== '' && Number(value) > 1000) {
        return;
      }
      setNewCourse((prev: any) => ({
        ...prev,
        [field]: value === '' ? '' : Number(value)
      }));
    }
  }, [setNewCourse]);

  const handleComboboxChange = useCallback((value: string, field: string) => {
    setNewCourse((prev: any) => ({ ...prev, [field]: value }));
  }, [setNewCourse]);

  const getFieldError = useCallback((field: string) => {
    if (field === 'hours') {
      if (newCourse.hours === '') return 'Hours are required';
      if (Number(newCourse.hours) === 0) return 'Hours must be greater than 0';
      if (Number(newCourse.hours) > 100) return 'Hours cannot exceed 100';
    }
    if (field === 'max_participants') {
      if (Number(newCourse.max_participants) > 1000) return 'Maximum participants cannot exceed 1000';
      if (Number(newCourse.max_participants) < 0) return 'Maximum participants cannot be negative';
    }
    return '';
  }, [newCourse.hours, newCourse.max_participants]);

  return (
    <Card className="p-6 border-accent-light/20">
      {/* Event Banner Upload at the top */}
      <div className="mb-6">
        <Label className="text-base font-serif">Event Banner Image</Label>
        <Input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            // Upload to Supabase Storage
            const filePath = `banners/${Date.now()}_${file.name}`;
            const { data, error } = await supabase.storage.from('course-images').upload(filePath, file);
            if (!error) {
              const { data } = supabase.storage.from('course-images').getPublicUrl(filePath);
              setNewCourse((prev: any) => ({ ...prev, event_banner_url: data.publicUrl }));
            }
          }}
        />
        {newCourse.event_banner_url && (
          <img src={newCourse.event_banner_url} alt="Event Banner" className="mt-2 h-28 w-full object-cover rounded-lg border" />
        )}
      </div>
      <div className="flex items-center gap-2 mb-6">
        <GraduationCap className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-title font-semibold text-primary">Basic Information</h2>
      </div>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label className="text-base font-serif">Title *</Label>
          <Input
            value={newCourse.title}
            onChange={(e) => handleTextChange(e, 'title')}
            placeholder="Enter course title"
            className="text-lg font-serif"
          />
          {!newCourse.title && (
            <p className="text-sm text-destructive mt-1 font-sans">Title is required</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-base font-serif">Description</Label>
          <Textarea
            value={newCourse.description}
            onChange={(e) => handleTextChange(e, 'description')}
            placeholder="Provide a detailed description of the course"
            className="min-h-[120px] font-serif"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-base font-serif">CEU Hours</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                value={newCourse.hours || 0}
                disabled={true}
                className="pl-10 bg-muted cursor-not-allowed font-sans"
              />
            </div>
            <p className="text-sm text-muted-foreground font-caption">Hours are calculated from selected State Requirements</p>
          </div>
          <div className="space-y-2">
            <Label className="text-base font-serif">Max Participants</Label>
            <div className="relative">
              <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                value={newCourse.max_participants || ''}
                onChange={(e) => handleNumberChange(e, 'max_participants')}
                placeholder="Leave empty for unlimited"
                min="0"
                max="1000"
                className={`pl-10 font-sans ${getFieldError('max_participants') ? 'border-destructive' : ''}`}
              />
            </div>
            {getFieldError('max_participants') && (
              <p className="text-sm text-destructive mt-1 font-sans">{getFieldError('max_participants')}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-base font-serif">Presenter *</Label>
            <Input
              value={newCourse.presenter}
              onChange={(e) => handleTextChange(e, 'presenter')}
              placeholder="Course presenter name"
              className="font-serif"
            />
            {!newCourse.presenter && (
              <p className="text-sm text-destructive mt-1 font-sans">Presenter is required</p>
            )}
            {/* Presenter Bio */}
            <Label className="text-base font-serif mt-4">About the Presenter</Label>
            <Textarea
              value={newCourse.presenter_bio || ''}
              onChange={(e) => handleTextChange(e, 'presenter_bio')}
              placeholder="Write a short bio about the presenter (optional)"
              className="min-h-[80px] font-serif"
            />
            {/* Presenter Image Upload */}
            <Label className="text-base font-serif mt-4">Presenter Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                // Upload to Supabase Storage
                const filePath = `presenters/${Date.now()}_${file.name}`;
                const { data, error } = await supabase.storage.from('course-images').upload(filePath, file);
                if (!error) {
                  const { data } = supabase.storage.from('course-images').getPublicUrl(filePath);
                  setNewCourse((prev: any) => ({ ...prev, presenter_image_url: data.publicUrl }));
                }
              }}
            />
            {/* Always show preview if presenter_image_url is set */}
            {newCourse.presenter_image_url && (
              <img src={newCourse.presenter_image_url} alt="Presenter Preview" className="mt-2 h-20 w-20 object-cover rounded-full border" />
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-base font-serif">Location</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                value={newCourse.location}
                onChange={(e) => handleTextChange(e, 'location')}
                placeholder="Course location"
                defaultValue="NWSISD Office Suite"
                className="pl-10 font-serif"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-base font-serif">Event Type *</Label>
            <Combobox
              options={eventTypeOptions}
              value={newCourse.event_type}
              onChange={(value) => handleComboboxChange(value, 'event_type')}
              placeholder="Select event type"
            />
            {!newCourse.event_type && (
              <p className="text-sm text-destructive mt-1 font-sans">Event type is required</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-base font-serif">Target Audience *</Label>
            <Combobox
              options={targetAudienceOptions}
              value={newCourse.target_audience}
              onChange={(value) => handleComboboxChange(value, 'target_audience')}
              placeholder="Select target audience"
            />
            {!newCourse.target_audience && (
              <p className="text-sm text-destructive mt-1 font-sans">Target audience is required</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
